export interface BeeRegistration {
  name: string;
  entryPath: string;
  validationModelPath?: string;
  capabilities: string[];
  type: 'fast' | 'standard' | 'slow';
}

export interface MemoryEntry {
  id: string;
  key: string;
  value: any;
  timestamp: string;
  metadata?: Record<string, any>;
}

class BrowserMemoryStore {
  private bees: Map<string, BeeRegistration> = new Map();
  private memory: Map<string, MemoryEntry> = new Map();
  private storageKey = 'hive-memory-store';

  constructor() {
    this.loadFromStorage();
  }

  registerBee(bee: BeeRegistration): void {
    this.bees.set(bee.name, bee);
    console.log(`[Browser MemoryStore] Registered bee: ${bee.name}`);
  }

  getBee(name: string): BeeRegistration | undefined {
    return this.bees.get(name);
  }

  getAllBees(): BeeRegistration[] {
    return Array.from(this.bees.values());
  }

  store(key: string, value: any, metadata?: Record<string, any>): void {
    const entry: MemoryEntry = {
      id: crypto.randomUUID(),
      key,
      value,
      timestamp: new Date().toISOString(),
      metadata
    };
    
    this.memory.set(key, entry);
    this.saveToStorage();
  }

  retrieve(key: string): any {
    const entry = this.memory.get(key);
    return entry?.value;
  }

  delete(key: string): boolean {
    const deleted = this.memory.delete(key);
    if (deleted) {
      this.saveToStorage();
    }
    return deleted;
  }

  clear(): void {
    this.memory.clear();
    this.saveToStorage();
  }

  getAllKeys(): string[] {
    return Array.from(this.memory.keys());
  }

  saveArtifact(key: string, artifact: any): void {
    this.store(key, artifact, { type: 'artifact' });
  }

  saveHiveMindTrace(trace: any): void {
    try {
      const scope = trace.scope || trace.key.split(':')[0];
      const traces = this.getHiveMindTraces(scope);
      traces.push(trace);
      localStorage.setItem(`hivemind:${scope}`, JSON.stringify(traces));
      console.log(`[Browser MemoryStore] HiveMind trace saved: ${trace.id}`);
    } catch (error) {
      console.error('Failed to save HiveMind trace:', error);
    }
  }

  getHiveMindTraces(scope: string): any[] {
    try {
      const data = localStorage.getItem(`hivemind:${scope}`);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to get HiveMind traces:', error);
      return [];
    }
  }

  async getTraces(scope: string): Promise<MemoryEntry[]> {
    const entries: MemoryEntry[] = [];
    for (const [key, entry] of this.memory.entries()) {
      if (key.startsWith(scope) || entry.metadata?.scope === scope) {
        entries.push(entry);
      }
    }
    return entries;
  }

  getRepoIndex(): any[] {
    const repoIndex = this.retrieve('repo-index');
    if (repoIndex && Array.isArray(repoIndex)) {
      return repoIndex;
    }
    
    return [
      {
        repoName: 'example-frontend',
        languages: ['TypeScript', 'React'],
        dependencies: { 'react': '^18.0.0', 'typescript': '^5.0.0' },
        tags: ['frontend', 'typescript']
      },
      {
        repoName: 'example-backend',
        languages: ['Node.js', 'TypeScript'],
        dependencies: { 'express': '^4.18.0', 'typescript': '^5.0.0' },
        tags: ['backend', 'api']
      },
      {
        repoName: 'beehive-core',
        languages: ['TypeScript', 'React'],
        dependencies: { 'react': '^18.0.0', 'typescript': '^5.0.0', 'tailwindcss': '^3.0.0' },
        tags: ['core', 'ui', 'typescript']
      }
    ];
  }

  private loadFromStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.memory = new Map(data.memory || []);
        this.bees = new Map(data.bees || []);
      }
    } catch (error) {
      console.warn('Failed to load memory store from localStorage:', error);
    }
  }

  private saveToStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const data = {
        memory: Array.from(this.memory.entries()),
        bees: Array.from(this.bees.entries()),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save memory store to localStorage:', error);
    }
  }
}

export const MemoryStore = new BrowserMemoryStore();
